import API from "@/utils/api";
import { parseRestaurantConfigurationResponse } from "@/domain/restaurant/restaurant.adapter";
import type {
  RestaurantConfigHttpEnvelope,
  RestaurantConfiguration,
} from "@/domain/restaurant/restaurant.types";
import { buildPublicRestaurantPath } from "@/services/public-storefront-paths";
import { usesHostOnlyPublicPaths } from "@/config/restaurant-id";

/** Public restaurant config path — mode-aware (PLATFORM_SLUG or CUSTOM_HOSTNAME). */
export function buildRestaurantConfigPath(slug?: string | null): string {
  return buildPublicRestaurantPath(slug);
}

/**
 * Fetches restaurant configuration from the public storefront restaurant route.
 * Does not send RestaurantId — tenant is established by the public storefront pipeline.
 *
 * @param slug Platform slug from PublicTenantProvider (PLATFORM_SLUG only).
 * @param expectedRestaurantId Bootstrap identity for response fallback / cache alignment only.
 */
export async function fetchRestaurantConfiguration(
  slug: string,
  expectedRestaurantId: string,
  signal?: AbortSignal
): Promise<RestaurantConfiguration> {
  const s = slug.trim();
  const rid = expectedRestaurantId.trim();
  if (!usesHostOnlyPublicPaths() && !s) {
    throw new Error("Slug is required");
  }
  if (!rid) {
    throw new Error("Restaurant id is required");
  }

  const path = buildRestaurantConfigPath(s);
  const response = await API.get<RestaurantConfigHttpEnvelope>(path, { signal });
  return parseRestaurantConfigurationResponse(response.data, rid);
}
